// Domain (view-model) layer for ProductInformation.
//
// Adapts the truthful, wire-shaped API contract (`TProductInformation` — all
// strings, all optional) into the richer shape the client works with: real
// `Date`s, a UI-agnostic colour union, and the relations returned via `include`.
// `toProductInformationVM` is the single boundary where wire types become domain
// types, so the rich types are backed by an actual conversion rather than asserted.
//
// Field-by-field rationale: docs/migrations/contracts/ProductInformation.md
import { TProductInformation } from '@vidende/contracts';
// Relations are not modelled on the API contract (they are LoopBack relations),
// so they are carried with their hand-rolled client types (contracts
// client-types) until each gets its own contract migration.
import type {
    ProductSubjectivity,
    ProductAction,
    ProductMaterialChange,
    entityAttachment,
} from '@vidende/contracts';

/**
 * UI-agnostic colour union (the Semantic UI palette literals, declared here so the
 * data layer does not import `semantic-ui-react`). A subset of `SemanticCOLORS`,
 * so it remains assignable to Semantic's `color` prop.
 */
export type StatusColor =
    | 'red'
    | 'orange'
    | 'yellow'
    | 'olive'
    | 'green'
    | 'teal'
    | 'blue'
    | 'violet'
    | 'purple'
    | 'pink'
    | 'brown'
    | 'grey'
    | 'black';

const STATUS_COLORS: readonly StatusColor[] = [
    'red',
    'orange',
    'yellow',
    'olive',
    'green',
    'teal',
    'blue',
    'violet',
    'purple',
    'pink',
    'brown',
    'grey',
    'black',
];

/** Narrow an arbitrary wire string to a `StatusColor`, or `undefined` if unknown. */
export const toStatusColor = (
    value?: string | null,
): StatusColor | undefined =>
    value != null && (STATUS_COLORS as readonly string[]).includes(value)
        ? (value as StatusColor)
        : undefined;

/** Convert an ISO date-time string to a `Date`, or `undefined` when absent. */
const toDate = (value?: string | null): Date | undefined =>
    value ? new Date(value) : undefined;

/** Fields whose wire type is converted on the way into the domain type. */
type ConvertedFields =
    | 'lastReviewDate'
    | 'nextReviewDue'
    | 'manufacturerProgrammeColor'
    | 'productRiskAssessmentProgrammeColor'
    | 'productOutcome1ProgrammeColor'
    | 'productOutcome2ProgrammeColor'
    | 'productOutcome3ProgrammeColor'
    | 'productOutcome4ProgrammeColor'
    | 'fairValueColor'
    | 'governanceProgrammeColor';

/** The raw API payload: the contract plus the relations returned via `include`. */
export type RawProductInformation = TProductInformation & {
    productSubjectivities?: ProductSubjectivity[];
    productActions?: ProductAction[];
    productMaterialChanges?: ProductMaterialChange[];
    entityAttachments?: entityAttachment[];
};

/** Client-facing ProductInformation — contract fields with richer domain types. */
export interface ProductInformationVM extends Omit<
    TProductInformation,
    ConvertedFields
> {
    lastReviewDate?: Date;
    nextReviewDue?: Date;
    manufacturerProgrammeColor?: StatusColor;
    productRiskAssessmentProgrammeColor?: StatusColor;
    productOutcome1ProgrammeColor?: StatusColor;
    productOutcome2ProgrammeColor?: StatusColor;
    productOutcome3ProgrammeColor?: StatusColor;
    productOutcome4ProgrammeColor?: StatusColor;
    fairValueColor?: StatusColor;
    governanceProgrammeColor?: StatusColor;
    productSubjectivities: ProductSubjectivity[];
    productActions: ProductAction[];
    productMaterialChanges: ProductMaterialChange[];
    entityAttachments: entityAttachment[];
}

/** Boundary converter: raw API payload -> `ProductInformation` domain type. */
export const toProductInformationVM = (
    raw: RawProductInformation,
): ProductInformationVM => ({
    ...raw,
    lastReviewDate: toDate(raw.lastReviewDate),
    nextReviewDue: toDate(raw.nextReviewDue),
    manufacturerProgrammeColor: toStatusColor(raw.manufacturerProgrammeColor),
    productRiskAssessmentProgrammeColor: toStatusColor(
        raw.productRiskAssessmentProgrammeColor,
    ),
    productOutcome1ProgrammeColor: toStatusColor(
        raw.productOutcome1ProgrammeColor,
    ),
    productOutcome2ProgrammeColor: toStatusColor(
        raw.productOutcome2ProgrammeColor,
    ),
    productOutcome3ProgrammeColor: toStatusColor(
        raw.productOutcome3ProgrammeColor,
    ),
    productOutcome4ProgrammeColor: toStatusColor(
        raw.productOutcome4ProgrammeColor,
    ),
    fairValueColor: toStatusColor(raw.fairValueColor),
    governanceProgrammeColor: toStatusColor(raw.governanceProgrammeColor),
    productSubjectivities: raw.productSubjectivities ?? [],
    productActions: raw.productActions ?? [],
    productMaterialChanges: raw.productMaterialChanges ?? [],
    entityAttachments: raw.entityAttachments ?? [],
});
